/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ReviewMode - Tinder-Style Image Approval
 *
 * Single-image review interface for focused approval workflow.
 * Shows large image with properties sidebar and action buttons.
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGallerySearch } from '@/hooks/useGalleryV2';
import { useApprovalActions, useApprovalStats } from '@/hooks/useApproval';
import { Button } from '@/components/v2/ui';

export function ReviewMode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch pending images only
  const { images, isLoading, updateParams, params } = useGallerySearch();
  const { updateSingle, isUpdatingSingle } = useApprovalActions();
  const { data: stats } = useApprovalStats();

  // Set approval_status filter to 'pending' on mount (only if not already set)
  useEffect(() => {
    const currentStatus = params.approval_status;
    const isPendingOnly = currentStatus && currentStatus.length === 1 && currentStatus[0] === 'pending';

    if (!isPendingOnly) {
      updateParams({ approval_status: ['pending'] });
    }
  }, [params.approval_status, updateParams]);

  // Set initial index based on start_image URL parameter
   
  useEffect(() => {
    const startImageId = searchParams.get('start_image');
    if (startImageId && images.length > 0) {
      const imageIndex = images.findIndex((img) => img.id === startImageId);
      if (imageIndex !== -1) {
        setCurrentIndex(imageIndex);
      }
    }
  }, [searchParams, images]);

  // Adjust index if it's out of bounds after list update
   
  useEffect(() => {
    if (currentIndex >= images.length && images.length > 0) {
      setCurrentIndex(images.length - 1);
    }
  }, [images.length, currentIndex]);

  const currentImage = images[currentIndex] || null;

  const handleClose = useCallback(() => {
    navigate('/v2/tools/gallery');
  }, [navigate]);

  const handleSkip = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleClose();
    }
  }, [currentIndex, images.length, handleClose]);

  const handleApprove = useCallback(() => {
    if (!currentImage) return;

    updateSingle(
      { imageId: currentImage.id, action: 'approve' },
      {
        onSuccess: () => {
          // Don't increment index - the approved image will be removed from the list
          // and the next image will automatically appear at the same index.
          // If this was the last image, close the modal.
          if (currentIndex >= images.length - 1) {
            handleClose();
          }
          // Otherwise, stay at the same index (next image will appear here after refetch)
        },
      }
    );
  }, [currentImage, currentIndex, images.length, updateSingle, handleClose]);

  const handleReject = useCallback(() => {
    if (!currentImage) return;

    updateSingle(
      { imageId: currentImage.id, action: 'reject' },
      {
        onSuccess: () => {
          // Don't increment index - the rejected image will be removed from the list
          // and the next image will automatically appear at the same index.
          // If this was the last image, close the modal.
          if (currentIndex >= images.length - 1) {
            handleClose();
          }
          // Otherwise, stay at the same index (next image will appear here after refetch)
        },
      }
    );
  }, [currentImage, currentIndex, images.length, updateSingle, handleClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isUpdatingSingle) return;

      if (e.key === 'ArrowRight') {
        handleApprove();
      } else if (e.key === 'ArrowLeft') {
        handleSkip();
      } else if (e.key === 'ArrowDown') {
        handleReject();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentImage, isUpdatingSingle, handleApprove, handleSkip, handleReject, handleClose]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-neutral-600 dark:text-neutral-400">
          {t('common.loading2')}
        </div>
      </div>
    );
  }

  if (!currentImage) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          Keine Bilder zur Freigabe
        </div>
        <Button onClick={handleClose}>Zurück zur Galerie</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <header className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                Bild-Freigabe
              </h1>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {currentIndex + 1} von {images.length} Bildern{' '}
                {stats && `• ${stats.pending} offen`}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="flex gap-6 items-center max-w-7xl w-full">
          {/* Left: Image */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={currentImage.image_url || ''}
              alt={currentImage.alt_title || currentImage.prompt}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-xl"
            />
          </div>

          {/* Right: Properties */}
          <div className="w-80 bg-white dark:bg-neutral-900 rounded-lg p-6 border border-neutral-200 dark:border-neutral-800">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Eigenschaften
            </h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-neutral-600 dark:text-neutral-400">Prompt:</dt>
                <dd className="text-neutral-900 dark:text-neutral-100 mt-1">
                  {currentImage.prompt}
                </dd>
              </div>
              {currentImage.ratio && (
                <div>
                  <dt className="text-neutral-600 dark:text-neutral-400">Format:</dt>
                  <dd className="text-neutral-900 dark:text-neutral-100 mt-1">
                    {currentImage.ratio}
                  </dd>
                </div>
              )}
              {currentImage.template_name && (
                <div>
                  <dt className="text-neutral-600 dark:text-neutral-400">Template:</dt>
                  <dd className="text-neutral-900 dark:text-neutral-100 mt-1">
                    {currentImage.template_name}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-600 dark:text-neutral-400">Erstellt:</dt>
                <dd className="text-neutral-900 dark:text-neutral-100 mt-1">
                  {new Date(currentImage.created_at).toLocaleDateString('de-DE')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </main>

      {/* Footer: Action Buttons */}
      <footer className="flex-shrink-0 px-6 py-6 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="flex items-center justify-center gap-6">
          {/* Skip Button */}
          <Button
            variant="outline"
            size="lg"
            onClick={handleSkip}
            disabled={isUpdatingSingle}
            className="flex items-center gap-2 min-w-[150px]"
          >
            <ArrowLeft className="h-5 w-5" />
            Überspringen
          </Button>

          {/* Reject Button */}
          <Button
            variant="outline"
            size="lg"
            onClick={handleReject}
            disabled={isUpdatingSingle}
            className="flex items-center gap-2 min-w-[150px] border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <ArrowDown className="h-5 w-5" />
            {t('common.reject')}
          </Button>

          {/* Approve Button */}
          <Button
            variant="default"
            size="lg"
            onClick={handleApprove}
            disabled={isUpdatingSingle}
            className="flex items-center gap-2 min-w-[150px] bg-green-600 hover:bg-green-700"
          >
            {t('common.approve')}
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Keyboard Hints */}
        <div className="flex items-center justify-center gap-6 mt-4 text-xs text-neutral-500 dark:text-neutral-500">
          <span>← Überspringen</span>
          <span>↓ Ablehnen</span>
          <span>→ Freigeben</span>
          <span>ESC Schließen</span>
        </div>
      </footer>
    </div>
  );
}

export default ReviewMode;
